/**
 * Hook to fetch all companies and those associated with a specific fund
 */
import { useQuery } from '@tanstack/react-query'

import { graphql_call as graphql_admin_call } from '../../schemas/server_admin_query.ts'

import {
  query_admin_fund_asset_navs,
  query_admin_fund_navs,
} from './fund_graphql.ts'
import type { FundAssetNavsResponse, FundNavsResponse } from './fund_queries.ts'
import { keys } from './keystore.ts'

export type FundAssetNavsRequest = {
  fundId: string
  startDate?: string
  endDate?: string
  effective_at?: string
}

/** gather all the navs for a fund (using new nav structure) */
export function useAdminFundNavs({
  fundId,
  startDate,
  endDate,
}: FundAssetNavsRequest) {
  const maxResults = 2000
  return useQuery({
    queryKey: keys.admin_funds.navs(fundId, { startDate, endDate }).queryKey,
    queryFn: async (): Promise<FundNavsResponse | null> => {
      try {
        if (!fundId) return null
        const response = await graphql_admin_call({
          query: query_admin_fund_navs,
          variables: {
            id: fundId,
            count: maxResults,
            ...(startDate && { start_date: startDate }),
            ...(endDate && { end_date: endDate }),
          },
        })
        if (!response) return null
        return response as FundNavsResponse
      } catch (error) {
        console.error('Error fetching fund navs:', error)
        return null
      }
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    enabled: Boolean(fundId),
  })
}

/** gather all the asset navs for a fund (using new nav structure) */
export function useAdminFundAssetNavs({
  fundId,
  startDate,
  endDate,
  effective_at,
}: FundAssetNavsRequest) {
  const maxResults = 2000
  return useQuery({
    queryKey: keys.admin_funds.assetNavs(fundId, {
      startDate,
      endDate,
      effective_at,
    }).queryKey,
    // eslint-disable-next-line complexity
    queryFn: async (): Promise<FundAssetNavsResponse | null> => {
      try {
        if (!fundId) return null
        const response = await graphql_admin_call({
          query: query_admin_fund_asset_navs,
          variables: {
            id: fundId,
            count: maxResults,
            ...(startDate && { start_date: startDate }),
            ...(endDate && { end_date: endDate }),
            ...(effective_at && { effective_at: effective_at }),
          },
        })
        if (!response) return null
        return response as FundAssetNavsResponse
      } catch (error) {
        console.error('Error fetching fund asset navs:', error)
        return null
      }
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    enabled: Boolean(fundId),
  })
}
